import moment from 'moment';
import {
    appSignOffMethodKey,
    emailSignOffMethodKey,
    fixedScheduleModeKey,
    getReviewTriggerType,
    getTopicIdByKey,
    getTriggerTypesForTopics,
    getTriggerValidationRules,
    isTriggerSaved,
    mapTriggersFromApi,
    onScreenSignOffMethodKey,
    physicalSignOffMethodKey,
    rollingScheduleModeKey,
    tachoInfringementsTopicKey,
    toTriggerRequest,
    toUnregisteredTriggerRequest
} from 'pages/employee-reviews/shared';

// Frontend copy for the employee sign-off method rows, keyed by the API's method key.
export const signOffMethodDescriptions = {
    [physicalSignOffMethodKey]: "Print and sign a PDF copy",
    [onScreenSignOffMethodKey]: "Employee enters their signature on screen during the review",
    [emailSignOffMethodKey]: "Email a temporary link to the driver for electronic sign-off",
    [appSignOffMethodKey]: "Employee can view and sign off the review via the app"
};

// Frontend copy for the schedule type options, keyed by the API's schedule mode key.
export const scheduleModeDescriptions = {
    [rollingScheduleModeKey]: "Each employee's next review is due one interval after their last completed review. Employees who have not been reviewed are first due on the start date, or on their own start date if later.",
    [fixedScheduleModeKey]: "Everyone is due on the start date and every interval after it."
};

export const scheduleModeDisplayOrder = [rollingScheduleModeKey, fixedScheduleModeKey];

// What a save does to each employee's due date when a review type returns to scheduling,
// keyed by the API's schedule mode key.
export const scheduleRestartTexts = {
    [rollingScheduleModeKey]: "When you save, employees who have been reviewed before will be due one interval after their last completed review, or on the new start date if that is later. Everyone else will be due on the new start date, or on their own start date if later.",
    [fixedScheduleModeKey]: "When you save, every employee this review type applies to will become due on the new start date and then repeat at the frequency you have set, regardless of when they were last reviewed."
};

// A missing or unrecognised mode is described as fixed, matching the mode buildApiModel
// sends when the review type holds none.
export function getScheduleRestartText(mode) {
    return scheduleRestartTexts[mode] ?? scheduleRestartTexts[fixedScheduleModeKey];
}

export const maxSignOffAlertDelayValue = 1000;

// The sign-off alert wait reuses the advance notice units. That lookup has no key column to
// resolve against, so days is referenced by id: 1 is days in lck.Review_Advance_Notice_Unit,
// which is also the default the Employee microservice stores when no unit is supplied.
export const defaultSignOffAlertDelayUnitId = 1;

// A completed review is only ever left awaiting the employee's signature when the
// signature was handed to them, and the Email and App methods are the only ones that do
// that - a physical or on-screen signature is taken at the review itself.
export function hasEmployeeDrivenSignOff(reviewType, reviewOptions) {
    const employeeDrivenMethodIds = (reviewOptions?.signOffMethods ?? [])
        .filter((method) => method.key === emailSignOffMethodKey || method.key === appSignOffMethodKey)
        .map((method) => method.id);

    return (reviewType?.signOffMethodIds ?? []).some((id) => employeeDrivenMethodIds.includes(id));
}

function emptyValueToNull(value) {
    if (value === "" || value === undefined) {
        return null;
    }
    return value;
}

export const includeItemsFromRequiredMessage = "Enter the date to include infringements from.";

const calendarDateFormat = "YYYY-MM-DD";

// The include-from date is stored as a SQL DATE, so the editor holds and sends it as a plain
// calendar date. The API returns it as "yyyy-MM-dd HH:mm:ss" with no offset, which moment reads
// as local time, and the picker hands back the local midnight of the chosen day, so formatting
// in local time keeps the day the user sees.
export function toCalendarDate(value) {
    if (value == null || value === "") {
        return null;
    }

    return moment(value).format(calendarDateFormat);
}

export function getTachoInfringementsTopicId(reviewOptions) {
    return (reviewOptions?.topics ?? []).find((topic) => topic.key === tachoInfringementsTopicKey)?.id ?? null;
}

export function isTachoInfringementsTopicSelected(reviewType, reviewOptions) {
    const topicId = getTachoInfringementsTopicId(reviewOptions);

    return topicId != null && (reviewType?.topicIds ?? []).includes(topicId);
}

// Mark all as reviewed starts off so it is only ever allowed by an administrator's choice.
export function buildDefaultTachoTopicSettings() {
    return {
        includeItemsFrom: null,
        allowMarkAllReviewed: false
    };
}

function mapTachoTopicSettingsFromApi(topicSettings, reviewOptions) {
    const topicId = getTachoInfringementsTopicId(reviewOptions);
    const setting = (topicSettings ?? []).find((item) => topicId != null && item.topicId === topicId);

    if (setting == null) {
        return buildDefaultTachoTopicSettings();
    }

    return {
        includeItemsFrom: toCalendarDate(setting.includeItemsFrom),
        allowMarkAllReviewed: setting.allowMarkAllReviewed === true
    };
}

// allowMarkAllReviewed is always sent because an update replaces the settings in full, so
// leaving it out would switch it off.
function buildTopicSettings(reviewType, reviewOptions) {
    if (!isTachoInfringementsTopicSelected(reviewType, reviewOptions)) {
        return [];
    }

    return [
        {
            topicId: getTachoInfringementsTopicId(reviewOptions),
            includeItemsFrom: toCalendarDate(reviewType.tachoTopicSettings?.includeItemsFrom),
            allowMarkAllReviewed: reviewType.tachoTopicSettings?.allowMarkAllReviewed === true
        }
    ];
}

// A trigger type is offered only while its topic is on the review type.
export function getOfferedTriggerTypes(reviewType, reviewOptions) {
    return getTriggerTypesForTopics(reviewType?.topicIds ?? [], reviewOptions);
}

// A trigger type the editor has no condition editor for cannot be shown or edited, but the save
// replaces every trigger, so it is held as the API sent it and sent back unchanged.
function mapUnregisteredTriggersFromApi(apiTriggers, reviewOptions) {
    return (apiTriggers ?? [])
        .filter((apiTrigger) => getReviewTriggerType(apiTrigger.triggerKey) == null)
        .map((apiTrigger) => {
            return {
                topicId: getTopicIdByKey(apiTrigger.topicKey, reviewOptions),
                apiTrigger: toUnregisteredTriggerRequest(apiTrigger)
            };
        });
}

// The triggers a save keeps: a trigger only belongs to a review type that has its topic.
export function getSavedTriggers(reviewType) {
    const topicIds = reviewType?.topicIds ?? [];

    return (reviewType?.triggers ?? [])
        .filter((trigger) => getReviewTriggerType(trigger.triggerKey) != null && topicIds.includes(trigger.topicId));
}

function buildTriggers(reviewType, reviewOptions) {
    const topicIds = reviewType?.topicIds ?? [];

    const registeredTriggers = getSavedTriggers(reviewType).map((trigger) => toTriggerRequest(trigger, reviewOptions));

    const unregisteredTriggers = (reviewType?.unregisteredTriggers ?? [])
        .filter((trigger) => topicIds.includes(trigger.topicId))
        .map((trigger) => trigger.apiTrigger);

    return [...registeredTriggers, ...unregisteredTriggers];
}

// Once a save succeeds every row holds what was saved. A new or changed row's added date is not
// known until the review type is read back, so it is cleared rather than left showing the date
// of the condition it replaced.
export function markTriggersSaved(reviewType) {
    return {
        ...reviewType,
        triggers: (reviewType?.triggers ?? []).map((trigger) => {
            if (isTriggerSaved(trigger)) {
                return trigger;
            }

            return {
                ...trigger,
                loadedCondition: trigger.condition,
                createdDate: null,
                addedWithReviewType: false
            };
        })
    };
}

// The five outcome levels are a fixed set: only the names are editable, so the
// editor model is seeded from the option defaults (colours stay on the options).
export function buildDefaultReviewType(reviewOptions) {
    return {
        name: "",
        enabled: true,
        schedule: {
            active: true,
            mode: rollingScheduleModeKey,
            startDate: null,
            frequency: null,
            frequencyUnitId: null,
            advanceNoticeValue: null,
            advanceNoticeUnitId: null
        },
        filterByLocations: false,
        requireManagerSignature: false,
        outcomeLevelsActive: true,
        outcomeLevelsMandatory: false,
        allowCustomNextReviewDate: false,
        raiseAlertWhenPending: false,
        raiseAlertForSignOff: false,
        signOffAlertDelayValue: 0,
        signOffAlertDelayUnitId: defaultSignOffAlertDelayUnitId,
        employeeTypes: [],
        employeeCategories: [],
        employeeStatuses: [],
        employeeLocations: [],
        signOffMethodIds: [],
        topicIds: [],
        tachoTopicSettings: buildDefaultTachoTopicSettings(),
        triggers: [],
        unregisteredTriggers: [],
        levels: (reviewOptions?.outcomeLevels ?? []).map((level) => {
            return {
                levelNumber: level.number,
                levelName: level.name
            };
        })
    };
}

export function mapIdsToOptions(ids, options) {
    if (ids == null || options == null) {
        return [];
    }

    return ids
        .map((id) => options.find((option) => option.id === id))
        .filter((option) => option != null);
}

// The API model carries id arrays for the applies-to pickers, but the editor works
// with the full option objects so the multi-selects can render names. The topic settings
// list is keyed by topic id, and the Tacho Infringements entry is held on its own so the
// Topics step can edit it directly.
export function mapReviewTypeFromApi(apiReviewType, employeeOptions, reviewOptions) {
    // An inactive review type is never scheduled, so its schedule is shown and saved as off.
    let scheduleActive = apiReviewType.schedule?.active;

    if (apiReviewType.enabled !== true) {
        scheduleActive = false;
    }

    return {
        ...apiReviewType,
        schedule: {
            ...apiReviewType.schedule,
            active: scheduleActive
        },
        employeeTypes: mapIdsToOptions(apiReviewType.employeeTypes, employeeOptions.employeeTypes),
        employeeCategories: mapIdsToOptions(apiReviewType.employeeCategories, employeeOptions.employeeCategories),
        employeeStatuses: mapIdsToOptions(apiReviewType.employeeStatuses, employeeOptions.employeeStatuses),
        employeeLocations: apiReviewType.employeeLocations ?? [],
        signOffMethodIds: apiReviewType.signOffMethodIds ?? [],
        topicIds: apiReviewType.topicIds ?? [],
        tachoTopicSettings: mapTachoTopicSettingsFromApi(apiReviewType.topicSettings, reviewOptions),
        triggers: mapTriggersFromApi(apiReviewType.triggers, reviewOptions),
        unregisteredTriggers: mapUnregisteredTriggersFromApi(apiReviewType.triggers, reviewOptions),
        levels: apiReviewType.levels ?? []
    };
}

export function buildApiModel(reviewType, reviewOptions) {
    // The API rejects the sign-off alert unless a sign-off method hands the signature to
    // the employee, so the value cannot be kept against a review type with no such method
    // selected.
    const raiseAlertForSignOff = reviewType.raiseAlertForSignOff === true && hasEmployeeDrivenSignOff(reviewType, reviewOptions);

    // The custom next review date acts on scheduled reviews, so it is only saved against a
    // schedulable review type.
    const allowCustomNextReviewDate = isSchedulable(reviewType) && reviewType.allowCustomNextReviewDate === true;

    return {
        name: reviewType.name,
        enabled: reviewType.enabled,
        schedule: {
            active: reviewType.schedule.active,
            mode: reviewType.schedule.mode ?? fixedScheduleModeKey,
            startDate: emptyValueToNull(reviewType.schedule.startDate),
            frequency: emptyValueToNull(reviewType.schedule.frequency),
            frequencyUnitId: emptyValueToNull(reviewType.schedule.frequencyUnitId),
            advanceNoticeValue: emptyValueToNull(reviewType.schedule.advanceNoticeValue),
            advanceNoticeUnitId: emptyValueToNull(reviewType.schedule.advanceNoticeUnitId)
        },
        filterByLocations: reviewType.filterByLocations,
        requireManagerSignature: reviewType.requireManagerSignature,
        outcomeLevelsActive: reviewType.outcomeLevelsActive,
        outcomeLevelsMandatory: reviewType.outcomeLevelsMandatory,
        allowCustomNextReviewDate: allowCustomNextReviewDate,
        raiseAlertWhenPending: reviewType.raiseAlertWhenPending === true,
        raiseAlertForSignOff: raiseAlertForSignOff,
        signOffAlertDelayValue: raiseAlertForSignOff ? reviewType.signOffAlertDelayValue : 0,
        signOffAlertDelayUnitId: raiseAlertForSignOff ? reviewType.signOffAlertDelayUnitId : defaultSignOffAlertDelayUnitId,
        employeeTypes: reviewType.employeeTypes.map((item) => item.id),
        employeeCategories: reviewType.employeeCategories.map((item) => item.id),
        employeeStatuses: reviewType.employeeStatuses.map((item) => item.id),
        employeeLocations: reviewType.filterByLocations ? reviewType.employeeLocations : [],
        signOffMethodIds: reviewType.signOffMethodIds,
        topicIds: reviewType.topicIds,
        topicSettings: buildTopicSettings(reviewType, reviewOptions),
        triggers: buildTriggers(reviewType, reviewOptions),
        levels: reviewType.levels.map((level) => {
            return {
                levelNumber: level.levelNumber,
                levelName: level.levelName
            };
        })
    };
}

// Approximate day lengths used to compare schedule durations client-side. The unit
// lookups only expose display names, so units are matched on their singular base name.
const unitDayLengths = { day: 1, week: 7, month: 30.4, year: 365 };

function getUnitDayLength(units, unitId) {
    const unit = (units ?? []).find((item) => item.id === unitId);

    if (unit?.name == null) {
        return null;
    }

    let baseName = unit.name.toLowerCase();

    if (baseName.endsWith("s")) {
        baseName = baseName.substring(0, baseName.length - 1);
    }

    return unitDayLengths[baseName] ?? null;
}

// A review type is only scheduled while it is both switched on and set to schedule -
// either switch off leaves it raised on demand.
export function isSchedulable(reviewType) {
    return reviewType?.enabled === true && reviewType?.schedule?.active === true;
}

// Mirrors what a save keeps: the custom next review date only applies to a schedulable
// review type, so the editor clears it once such a type is saved.
export function clearUnschedulableSettings(reviewType) {
    if (isSchedulable(reviewType)) {
        return reviewType;
    }

    return {
        ...reviewType,
        allowCustomNextReviewDate: false
    };
}

export function isTodayOrLater(date) {
    return date != null && !moment(date).isBefore(moment().startOf('day'));
}

// A null originalReviewType means create mode - a review type set up as scheduled from
// new is held to the same today-or-later rule as one returning to scheduling.
export function startDateMustBeFuture(reviewType, originalReviewType) {
    return isSchedulable(reviewType) && (originalReviewType == null || !isSchedulable(originalReviewType));
}

// Returning to scheduling is a transition an already saved review type makes, so it
// never applies to one being created.
export function isReturningToScheduling(reviewType, originalReviewType) {
    return originalReviewType != null && startDateMustBeFuture(reviewType, originalReviewType);
}

// Only a saved scheduled review type has pending reviews for a save to remove, so leaving
// scheduling never applies to one being created.
export function isLeavingScheduling(reviewType, originalReviewType) {
    return isSchedulable(originalReviewType) && !isSchedulable(reviewType);
}

export function getDetailsValidationConfig(reviewType, reviewOptions, originalReviewType = null) {
    const rules = [
        { fieldName: "name", required: true, maxLength: 200 },
        { fieldName: "employeeTypes", required: true },
        { fieldName: "employeeCategories", required: true },
        { fieldName: "employeeStatuses", required: true }
    ];

    const externalRules = [];

    // The schedule fields only become required when the schedule is active.
    if (reviewType.schedule.active) {
        rules.push({ fieldName: "schedule.startDate", required: true });
        rules.push({ fieldName: "schedule.frequency", required: true });
        rules.push({ fieldName: "schedule.frequencyUnitId", required: true });
        rules.push({ fieldName: "schedule.advanceNoticeValue", required: true });
        rules.push({ fieldName: "schedule.advanceNoticeUnitId", required: true });

        // Scheduling from a date in the past would make every employee overdue the
        // moment the reviews are raised. An absent date is left to the required rule.
        if (startDateMustBeFuture(reviewType, originalReviewType) && reviewType.schedule.startDate != null &&
            !isTodayOrLater(reviewType.schedule.startDate)) {
            externalRules.push({
                fieldName: "schedule.startDate",
                valid: false,
                errorMessage: "The start date must be today or later."
            });
        }

        // A review appears in Pending Reviews ahead of its due date by the advance
        // notice, so the notice window must fit inside a single review cycle.
        const frequencyDays = getUnitDayLength(reviewOptions?.frequencyUnits, reviewType.schedule.frequencyUnitId);
        const advanceNoticeDays = getUnitDayLength(reviewOptions?.advanceNoticeUnits, reviewType.schedule.advanceNoticeUnitId);

        if (reviewType.schedule.frequency != null && reviewType.schedule.advanceNoticeValue != null &&
            frequencyDays != null && advanceNoticeDays != null &&
            (reviewType.schedule.advanceNoticeValue * advanceNoticeDays) >= (reviewType.schedule.frequency * frequencyDays)) {
            externalRules.push({
                fieldName: "schedule.advanceNotice",
                valid: false,
                errorMessage: "Advance notice must be shorter than how often the review occurs."
            });
        }
    }

    if (reviewType.filterByLocations && (reviewType.employeeLocations == null || reviewType.employeeLocations.length === 0)) {
        externalRules.push({
            fieldName: "employeeLocations",
            valid: false,
            errorMessage: "At least one location must be selected if filtering by location."
        });
    }

    return { rules, externalRules };
}

export function getTopicsValidationConfig(reviewType, reviewOptions) {
    const externalRules = [];

    if (reviewType.topicIds.length === 0) {
        externalRules.push({
            fieldName: "topicIds",
            valid: false,
            errorMessage: "At least one topic must be selected."
        });
    }

    if (isTachoInfringementsTopicSelected(reviewType, reviewOptions) && toCalendarDate(reviewType.tachoTopicSettings?.includeItemsFrom) == null) {
        externalRules.push({
            fieldName: "tachoTopicSettings.includeItemsFrom",
            valid: false,
            errorMessage: includeItemsFromRequiredMessage
        });
    }

    return { rules: [], externalRules };
}

// Only the triggers a save keeps are validated; a trigger whose topic has been deselected is
// dropped on save, so nothing is reported against it.
export function getTriggersValidationConfig(reviewType, reviewOptions) {
    return {
        rules: [],
        externalRules: getTriggerValidationRules(reviewType?.triggers ?? [], reviewOptions, { checkedTriggers: getSavedTriggers(reviewType) })
    };
}

export function getSignOffValidationConfig(reviewType, reviewOptions) {
    const rules = [];
    const externalRules = [];

    // The wait is only offered, and only means anything, while the sign-off alert is on
    // and a method leaves the signature with the employee.
    if (reviewType.raiseAlertForSignOff === true && hasEmployeeDrivenSignOff(reviewType, reviewOptions)) {
        rules.push({
            fieldName: "signOffAlertDelayValue",
            required: true,
            type: "number",
            minValue: 0,
            maxValue: maxSignOffAlertDelayValue
        });
        rules.push({
            fieldName: "signOffAlertDelayUnitId",
            required: true
        });
    }

    if (reviewType.signOffMethodIds.length === 0) {
        externalRules.push({
            fieldName: "signOffMethodIds",
            valid: false,
            errorMessage: "At least one sign-off method must be selected."
        });
    }

    const appSignOffMethodId = (reviewOptions?.signOffMethods ?? [])
        .find((method) => method.key === appSignOffMethodKey)?.id;

    if (appSignOffMethodId != null) {
        const hasAppSignOff = reviewType.signOffMethodIds.includes(appSignOffMethodId);
        const hasAlternative = reviewType.signOffMethodIds.some((id) => id !== appSignOffMethodId);

        if (hasAppSignOff && !hasAlternative) {
            externalRules.push({
                fieldName: "signOffMethodIds",
                valid: false,
                errorMessage: "App sign-off cannot be the only sign-off method selected."
            });
        }
    }

    if (reviewType.levels.some((level) => level.levelName == null || level.levelName.trim().length === 0)) {
        externalRules.push({
            fieldName: "levels",
            valid: false,
            errorMessage: "All levels must have a name."
        });
    }

    return { rules, externalRules };
}
